import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import type { AssetAssignment, Custodian } from '../api';
import { useTransferir, useLocations } from '../hooks';
import { formatLocationLabel } from '../../../shared/lib/locations';
import { CustodianAutocomplete } from './CustodianAutocomplete';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  assetId: string;
  onClose: () => void;
  onSuccess: (assignment: AssetAssignment) => void;
}

export function TransferenciaModal({ open, assetId, onClose, onSuccess }: Props) {
  const mutation = useTransferir(assetId);
  const locationsQuery = useLocations();
  const [custodian, setCustodian] = useState<Custodian | null>(null);
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [locationId, setLocationId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setCustodian(null);
    setOccurredAt(new Date().toISOString().slice(0, 10));
    setLocationId('');
    setReason(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!custodian) { setError('Selecione o novo responsável'); return; }
    try {
      const result = await mutation.mutateAsync({
        toCustodianId: custodian.id,
        occurredAt: new Date(occurredAt).toISOString(),
        locationId: locationId || undefined,
        reason: reason.trim() || undefined,
      });
      reset();
      onSuccess(result);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao transferir'));
    }
  }

  return (
    <>
      <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Transferir ativo</Dialog.Title>
            <p className="mt-1 text-xs text-gray-500">
              Fecha o vínculo atual e abre um novo com o responsável destino. Um novo termo de responsabilidade é gerado.
            </p>
            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Novo responsável *</label>
                <CustodianAutocomplete
                  value={custodian}
                  onChange={setCustodian}
                />
              </div>
              <TextInput
                label="Data da transferência *"
                type="date"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                required
              />
              <AutocompleteSelect
                label="Nova localização (opcional)"
                options={(locationsQuery.data?.data ?? []).map((l) => ({
                  value: l.id,
                  label: formatLocationLabel(l),
                }))}
                value={locationId}
                onChange={setLocationId}
              />
              <p className="-mt-2 text-xs text-gray-500">
                Só mude a localização se o ativo estiver sendo movido. Trocar o
                responsável não altera a localização automaticamente.
              </p>
              <Textarea
                label="Motivo (opcional)"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />

              {error && <Alert variant="error">{error}</Alert>}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
                <Button type="submit" loading={mutation.isPending}>
                  {mutation.isPending ? 'Transferindo…' : 'Transferir'}
                </Button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
